import React, { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button, Icon } from "@wfp/ui";
import { iconWfpLogoVerticalEn } from "@wfp/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { useReactToPrint } from "react-to-print";

import "./_dashboard-guide.scss";

type GraphTable = {
  dateHeaders: string[];
  rows: {
    label: string;
    values: string[];
    highlightedValueIndexes?: number[];
    max: string;
  }[];
  footerRow?: {
    values: string[];
    max: string;
  };
};

type GraphEntry = {
  titleId: string;
  titleDefault: string;
  descriptionId: string;
  descriptionDefault: string;
  formulaId: string;
  formulaDefault: string;
  table?: GraphTable;
};

type GuideSection = {
  sectionKey: string;
  titleDefault: string;
  descriptionDefault: string;
  graphs: GraphEntry[];
};

const GUIDE_SECTIONS: GuideSection[] = [
  {
    sectionKey: "enrolmentsAttendance",
    titleDefault: "Enrolments and Attendance",
    descriptionDefault:
      "These graphs track student enrolment, attendance, and beneficiary numbers over time, split by gender where applicable.",
    graphs: [
      {
        titleId: "analytics.guide.graph.currentEnrolment.title",
        titleDefault: "Current enrolments",
        descriptionId: "analytics.guide.graph.currentEnrolment.description",
        descriptionDefault:
          "Shows the enrolment count as recorded on the last school day of each month, split by boys and girls. This is not a monthly sum but the latest snapshot of enrolled students, useful for tracking the total student population over time.",
        formulaId: "analytics.guide.graph.currentEnrolment.formula",
        formulaDefault: "Enrolment count on the last school day of the month",
      },
      {
        titleId: "analytics.guide.graph.newEnrollees.title",
        titleDefault: "New enrollees",
        descriptionId: "analytics.guide.graph.newEnrollees.description",
        descriptionDefault:
          "Shows monthly new enrollees split by boys and girls. Useful for tracking student intake and growth.",
        formulaId: "analytics.guide.graph.newEnrollees.formula",
        formulaDefault:
          "Sum of students enrolled for the first time this month",
      },
      {
        titleId: "analytics.guide.graph.studentAttendance.title",
        titleDefault: "Student attendance",
        descriptionId: "analytics.guide.graph.studentAttendance.description",
        descriptionDefault:
          "Show the highest number of students recorded as present in class during the month.\n\n" +
          "To calculate this indicator, the system identifies the highest daily attendance recorded for each grade and gender during the month and then adds these values together.\n\n" +
          "Important: The highest attendance for different grades or genders may come from different days of the month. Therefore, the total attendance figure does not represent the number of students present on a single day, but rather the sum of the highest attendance recorded for each grade and gender.\n\n" +
          "The table below illustrates how the maximum attendance values are selected for each grade and gender before being summed into the monthly attendance indicator.",
        formulaId: "analytics.guide.graph.studentAttendance.formula",
        formulaDefault:
          "Sum of the highest daily attendance recorded for each grade and gender during the month",
        table: {
          dateHeaders: [
            "2025-03-01",
            "2025-03-02",
            "2025-03-03",
            "2025-03-04",
            "2025-03-05",
          ],
          rows: [
            {
              label: "Class 1 Boys",
              values: ["7", "9", "10", "9", "7"],
              highlightedValueIndexes: [2],
              max: "10",
            },
            {
              label: "Class 1 Girls",
              values: ["12", "12", "9", "11", "13"],
              highlightedValueIndexes: [4],
              max: "13",
            },
            {
              label: "Class 2 Boys",
              values: ["12", "14", "16", "12", "14"],
              highlightedValueIndexes: [2],
              max: "16",
            },
            {
              label: "Class 2 Girls",
              values: ["14", "15", "13", "12", "15"],
              highlightedValueIndexes: [1],
              max: "15",
            },
            {
              label: "Class 3 Boys",
              values: ["13", "14", "13", "16", "15"],
              highlightedValueIndexes: [3],
              max: "16",
            },
            {
              label: "Class 3 Girls",
              values: ["12", "13", "14", "13", "13"],
              highlightedValueIndexes: [2],
              max: "14",
            },
          ],
          footerRow: {
            values: ["70", "77", "75", "73", "77"],
            max: "84",
          },
        },
      },
      {
        titleId: "analytics.guide.graph.attendeesAverage.title",
        titleDefault: "Average attendees",
        descriptionId: "analytics.guide.graph.attendeesAverage.description",
        descriptionDefault:
          "Shows the monthly average number of students present per day, split by boys and girls. Calculated as the total attendees across all recorded days divided by the number of distinct days with attendance data. Useful for tracking typical daily participation rather than peak attendance.",
        formulaId: "analytics.guide.graph.attendeesAverage.formula",
        formulaDefault: "Sum of daily attendees / Number of attendance days",
      },
      {
        titleId: "analytics.guide.graph.beneficiaries.title",
        titleDefault: "Beneficiaries",
        descriptionId: "analytics.guide.graph.beneficiaries.description",
        descriptionDefault:
          "Shows students who received a school meal.\n\n" +
          "To calculate this indicator, the system identifies the highest daily number of students fed for each grade and gender during the month and then adds these values together.\n\n" +
          "Important: The highest number of beneficiaries for different grades or genders may come from different days of the month. Therefore, the total beneficiary figure does not represent the number of students fed on a single day, but rather the sum of the highest number of students fed for each grade and gender during the month.\n\n" +
          "The table below illustrates how the maximum beneficiary values are selected for each grade and gender before being summed into the monthly beneficiary indicator",
        formulaId: "analytics.guide.graph.beneficiaries.formula",
        formulaDefault:
          "Sum of the highest daily number of students fed for each grade and gender during the month",
        table: {
          dateHeaders: [
            "2025-03-01",
            "2025-03-02",
            "2025-03-03",
            "2025-03-04",
            "2025-03-05",
          ],
          rows: [
            {
              label: "Class 1 Boys",
              values: ["5", "6", "3", "8", "2"],
              highlightedValueIndexes: [3],
              max: "8",
            },
            {
              label: "Class 1 Girls",
              values: ["1", "7", "9", "11", "11"],
              highlightedValueIndexes: [3],
              max: "11",
            },
            {
              label: "Class 2 Boys",
              values: ["10", "6", "8", "10", "7"],
              highlightedValueIndexes: [0],
              max: "10",
            },
            {
              label: "Class 2 Girls",
              values: ["4", "12", "7", "8", "11"],
              highlightedValueIndexes: [1],
              max: "12",
            },
            {
              label: "Class 3 Boys",
              values: ["13", "11", "11", "12", "15"],
              highlightedValueIndexes: [4],
              max: "15",
            },
            {
              label: "Class 3 Girls",
              values: ["8", "9", "4", "12", "11"],
              highlightedValueIndexes: [3],
              max: "12",
            },
          ],
          footerRow: {
            values: ["41", "51", "42", "61", "57"],
            max: "68",
          },
        },
      },
      {
        titleId: "analytics.guide.graph.beneficiariesAverage.title",
        titleDefault: "Average beneficiaries",
        descriptionId: "analytics.guide.graph.beneficiariesAverage.description",
        descriptionDefault:
          "Shows the monthly average number of students who received meals per day, split by boys and girls. Calculated as the total beneficiaries across all meal days divided by the number of distinct days meals were served. Useful for understanding typical daily meal reach rather than peak beneficiary counts.",
        formulaId: "analytics.guide.graph.beneficiariesAverage.formula",
        formulaDefault: "Sum of daily beneficiaries / Number of meal days",
      },
      {
        titleId: "analytics.guide.graph.canteenAttendanceRate.title",
        titleDefault: "Canteen attendance rate",
        descriptionId:
          "analytics.guide.graph.canteenAttendanceRate.description",
        descriptionDefault:
          "Shows the percentage of enrolled students who received a school meal during the month.\n\n" +
          "The indicator is calculated by comparing highest daily number of students fed during the month with the current number of enrolled students.\n\n" +
          "100% means that all enrolled students received at least one meal during the month.\n" +
          "Less than 100% means that some enrolled students did not receive a meal during the month.\n" +
          "More than 100% may indicate a data quality issue or that meals were provided to students who were not included in the enrolment count.\n\n" +
          "Important: The beneficiary count used in this calculation represents the highest daily number of students fed during the month, not the total number of meals served throughout the month.",
        formulaId: "analytics.guide.graph.canteenAttendanceRate.formula",
        formulaDefault: "(Maximum beneficiaries / Current enrolments) * 100",
      },
    ],
  },
  {
    sectionKey: "commodities",
    titleDefault: "Commodities",
    descriptionDefault:
      "These graphs track the flow of food into and out of schools, including deliveries, purchases, spending, and price variance.",
    graphs: [
      {
        titleId: "analytics.guide.graph.kgDelivered.title",
        titleDefault: "Food Delivered (Kg)",
        descriptionId: "analytics.guide.graph.kgDelivered.description",
        descriptionDefault:
          "Shows the total quantity of food delivered to schools during the month, measured in kilograms.\n\n" +
          "This indicator tracks the flow of food supplies into schools and helps monitor whether schools are receiving the commodities needed to support the school meals programme.\n\n" +
          "Important: This indicator measures food delivered, not food consumed, distributed, or remaining in stock. A delivery is counted when in-kind food provided by WFP is received by the school.\n\n" +
          "Example:\n" +
          "Delivery 1: 500 kg\n" +
          "Delivery 2: 750 kg\n" +
          "Delivery 3: 250 kg\n\n" +
          "Food Delivered (kg) = 500 + 750 + 250 = 1,500 kg",
        formulaId: "analytics.guide.graph.kgDelivered.formula",
        formulaDefault:
          "Sum of all food quantities delivered to schools during the month",
      },
      {
        titleId: "analytics.guide.graph.kgPurchased.title",
        titleDefault: "Food Purchased (Kg)",
        descriptionId: "analytics.guide.graph.kgPurchased.description",
        descriptionDefault:
          "Shows the total quantity of food purchased during the month through a cash based transfer (CBT) modality, measured in kilograms.\n\n" +
          "This indicator tracks procurement volumes and helps monitor how much food has been purchased to support the school meals programme over time.\n\n" +
          "Important: This indicator measures food purchased, not food delivered, consumed, or remaining in stock. A purchase is counted when food is procured, regardless of when it is delivered or used.\n\n" +
          "Example:\n" +
          "Purchase 1: 1,000 kg\n" +
          "Purchase 2: 500 kg\n" +
          "Purchase 3: 750 kg\n\n" +
          "Food Purchased (kg) = 1,000 + 500 + 750 = 2,250 kg",
        formulaId: "analytics.guide.graph.kgPurchased.formula",
        formulaDefault: "Sum of all food quantities purchased during the month",
      },
      {
        titleId: "analytics.guide.graph.totalMoneySpent.title",
        titleDefault: "Total money spent",
        descriptionId: "analytics.guide.graph.totalMoneySpent.description",
        descriptionDefault:
          "Shows monthly spending split by food costs and other costs. Useful for budget monitoring and cost breakdown analysis.",
        formulaId: "analytics.guide.graph.totalMoneySpent.formula",
        formulaDefault: "Sum (Food cost) + Sum (Other cost)",
      },
      {
        titleId: "analytics.guide.graph.purchasePricesVariance.title",
        titleDefault: "Purchase prices variance",
        descriptionId:
          "analytics.guide.graph.purchasePricesVariance.description",
        descriptionDefault:
          "Shows the variance of purchase prices by commodity for the selected period. Each dot represents a distinct price paid, and the black mark indicates the average price per commodity. Filter by a single school for more detailed per-purchase data.",
        formulaId: "analytics.guide.graph.purchasePricesVariance.formula",
        formulaDefault: "",
      },
    ],
  },
  {
    sectionKey: "mealConsumption",
    titleDefault: "Meal Consumption",
    descriptionDefault:
      "These graphs track food consumption, meals served, contributions, and canteen functioning rates over time.",
    graphs: [
      {
        titleId: "analytics.guide.graph.kgConsumedTotal.title",
        titleDefault: "Total in-kind + purchased commodities consumed",
        descriptionId: "analytics.guide.graph.kgConsumedTotal.description",
        descriptionDefault:
          "Shows the monthly total kilograms of in-kind and purchased food consumed. Includes all food consumed, regardless of source.",
        formulaId: "analytics.guide.graph.kgConsumedTotal.formula",
        formulaDefault: "Sum (Distributed quantity * Unit conversion rate)",
      },
      {
        titleId: "analytics.guide.graph.kgConsumedInKind.title",
        titleDefault: "Total in-kind commodities consumed",
        descriptionId: "analytics.guide.graph.kgConsumedInKind.description",
        descriptionDefault:
          "Shows the monthly total kilograms of in-kind food consumed. Useful for tracking the contribution of in-kind food sources to meal programmes.",
        formulaId: "analytics.guide.graph.kgConsumedInKind.formula",
        formulaDefault:
          "Sum (Distributed quantity (in-kind) * Unit conversion rate)",
      },
      {
        titleId: "analytics.guide.graph.kgConsumedPurchased.title",
        titleDefault: "Total purchased commodities consumed",
        descriptionId: "analytics.guide.graph.kgConsumedPurchased.description",
        descriptionDefault:
          "Shows the monthly total kilograms of purchased food consumed. Useful for understanding the contribution of purchased food sources to meal programmes.",
        formulaId: "analytics.guide.graph.kgConsumedPurchased.formula",
        formulaDefault:
          "Sum (Distributed quantity (purchased) * Unit conversion rate)",
      },
      {
        titleId: "analytics.guide.graph.mealsServed.title",
        titleDefault: "Meals served",
        descriptionId: "analytics.guide.graph.mealsServed.description",
        descriptionDefault:
          "Shows the monthly total number of meals served. This equals the sum of beneficiaries across all meal days (each beneficiary receiving a meal on a given day counts as one meal). A key indicator of programme reach and meal delivery.",
        formulaId: "analytics.guide.graph.mealsServed.formula",
        formulaDefault: "Sum of daily beneficiaries across meal days",
      },
      {
        titleId: "analytics.guide.graph.contributionsServed.title",
        titleDefault: "Contributions Kg served",
        descriptionId: "analytics.guide.graph.contributionsServed.description",
        descriptionDefault:
          "Shows the monthly contributions served, expressed in kilograms. Contributions refer to in-kind food contributions received and served.",
        formulaId: "analytics.guide.graph.contributionsServed.formula",
        formulaDefault: "Sum (Contributions served * Unit conversion rate)",
      },
      {
        titleId: "analytics.guide.graph.averageRationPerStudent.title",
        titleDefault: "Average ration per student",
        descriptionId:
          "analytics.guide.graph.averageRationPerStudent.description",
        descriptionDefault:
          "Shows the monthly average ration consumed per student, expressed in kilograms. Helps monitor whether the food allocation per student is consistent and adequate across the selected period.",
        formulaId: "analytics.guide.graph.averageRationPerStudent.formula",
        formulaDefault: "Kg consumed in the canteen / Total beneficiaries",
      },
      {
        titleId: "analytics.guide.graph.schoolCanteenFunctioningAvg.title",
        titleDefault: "% School canteen functioning average",
        descriptionId:
          "analytics.guide.graph.schoolCanteenFunctioningAvg.description",
        descriptionDefault:
          "Shows the monthly school canteen functioning average as a percentage. The chart tooltip also includes the per-school average for comparison.",
        formulaId: "analytics.guide.graph.schoolCanteenFunctioningAvg.formula",
        formulaDefault: "(Meal days / Total school days) * 100",
      },
      {
        titleId: "analytics.guide.graph.filledSchoolDays.title",
        titleDefault: "% Filled school days",
        descriptionId: "analytics.guide.graph.filledSchoolDays.description",
        descriptionDefault:
          "Shows the monthly percentage of filled school days. A filled school day is one where the expected meal service was completed.",
        formulaId: "analytics.guide.graph.filledSchoolDays.formula",
        formulaDefault: "(Filled school days / Total school days) * 100",
      },
    ],
  },
  {
    sectionKey: "reporting",
    titleDefault: "Reporting",
    descriptionDefault:
      "These graphs track reporting completeness and school coverage, indicating data quality and participation across the selected period.",
    graphs: [
      {
        titleId: "analytics.guide.graph.approvedReportsPerSchool.title",
        titleDefault: "% Approved reports per school",
        descriptionId:
          "analytics.guide.graph.approvedReportsPerSchool.description",
        descriptionDefault:
          "Shows the percentage of reports approved per school each month. A low rate may indicate pending approvals or data quality issues that need follow-up.",
        formulaId: "analytics.guide.graph.approvedReportsPerSchool.formula",
        formulaDefault: "(Approved reports / Total reports) * 100",
      },
      {
        titleId: "analytics.guide.graph.activeSchools.title",
        titleDefault: "Number of active schools with school year data",
        descriptionId: "analytics.guide.graph.activeSchools.description",
        descriptionDefault:
          "Displays the count of schools that have submitted school year data for each month. Useful for understanding reporting coverage and how many schools are actively contributing data over time.",
        formulaId: "analytics.guide.graph.activeSchools.formula",
        formulaDefault: "",
      },
    ],
  },
];

const DashboardGuide: React.FC = () => {
  const intl = useIntl();
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: contentRef,
    documentTitle: intl
      .formatMessage({
        id: "analytics.guide.title",
        defaultMessage: "School Connect Analytics Dashboard Guide",
      })
      .toLowerCase()
      .replace(/\s+/g, "_"),
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 2cm 1.5cm 1.5cm;
      }
      @media print {
        body {
          color-adjust: exact;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
      }
    `,
    preserveAfterPrint: false,
    onPrintError: (errorLocation, error) => {
      console.log("Guide PDF generation error:", errorLocation, error);
    },
  });

  return (
    <div className="dashboard-guide">
      <Button
        kind="secondary"
        onClick={() => handlePrint()}
        className="dashboard-guide__button"
      >
        <FontAwesomeIcon icon={faFilePdf} className="dashboard-guide__icon" />
        <FormattedMessage
          id="analytics.guide.printButton"
          defaultMessage="Download guide (PDF)"
        />
      </Button>

      <div className="dashboard-guide__print-content" ref={contentRef}>
        <div className="dashboard-guide__header">
          <div className="dashboard-guide__logo">
            <Icon icon={iconWfpLogoVerticalEn} width={57} height={86} />
          </div>
          <div className="dashboard-guide__header-text">
            <h1 className="dashboard-guide__title">
              <FormattedMessage
                id="analytics.guide.title"
                defaultMessage="School Connect Analytics Dashboard Guide"
              />
            </h1>
            <p className="dashboard-guide__subtitle">
              <FormattedMessage
                id="analytics.guide.intro"
                defaultMessage="This document explains each graph displayed on the School Connect Analytics dashboard. Use it as a reference to interpret the data, understand what each metric represents, and how the headline numbers are calculated. You can save this guide as a PDF using your browser's print dialog."
              />
            </p>
          </div>
        </div>

        {GUIDE_SECTIONS.map((section, sectionIndex) => (
          <section
            key={section.sectionKey}
            className="dashboard-guide__section"
          >
            <h2 className="dashboard-guide__section-title">
              <span className="dashboard-guide__section-number">
                {sectionIndex + 1}
              </span>
              <FormattedMessage
                id={`analytics.guide.section.${section.sectionKey}.title`}
                defaultMessage={section.titleDefault}
              />
            </h2>
            <p className="dashboard-guide__section-description">
              <FormattedMessage
                id={`analytics.guide.section.${section.sectionKey}.description`}
                defaultMessage={section.descriptionDefault}
              />
            </p>
            <ul className="dashboard-guide__graph-list">
              {section.graphs.map((graph, graphIndex) => {
                const localeFormula = (intl.messages as Record<string, string>)[
                  graph.formulaId
                ];
                const formula = localeFormula || graph.formulaDefault;
                return (
                  <li
                    key={graph.titleId}
                    className="dashboard-guide__graph-item"
                  >
                    <div className="dashboard-guide__graph-heading">
                      <span className="dashboard-guide__graph-number">
                        {sectionIndex + 1}.{graphIndex + 1}
                      </span>
                      <h3 className="dashboard-guide__graph-title">
                        <FormattedMessage
                          id={graph.titleId}
                          defaultMessage={graph.titleDefault}
                        />
                      </h3>
                    </div>
                    <p className="dashboard-guide__graph-description">
                      <FormattedMessage
                        id={graph.descriptionId}
                        defaultMessage={graph.descriptionDefault}
                      />
                    </p>
                    {graph.table && (
                      <table className="dashboard-guide__graph-table">
                        <thead>
                          <tr>
                            <th></th>
                            {graph.table.dateHeaders.map((header, i) => (
                              <th key={i}>{header}</th>
                            ))}
                            <th>MAXIMUM REACHED</th>
                          </tr>
                        </thead>
                        <tbody>
                          {graph.table.rows.map((row, i) => (
                            <tr key={i}>
                              <td>{row.label}</td>
                              {row.values.map((cell, j) => (
                                <td
                                  key={j}
                                  className={
                                    row.highlightedValueIndexes?.includes(j)
                                      ? "dashboard-guide__graph-table-highlight"
                                      : undefined
                                  }
                                >
                                  {cell}
                                </td>
                              ))}
                              <td className="dashboard-guide__graph-table-max">
                                {row.max}
                              </td>
                            </tr>
                          ))}
                          {graph.table.footerRow && (
                            <tr className="dashboard-guide__graph-table-footer">
                              <td></td>
                              {graph.table.footerRow.values.map((cell, i) => (
                                <td key={i}>{cell}</td>
                              ))}
                              <td className="dashboard-guide__graph-table-max">
                                {graph.table.footerRow.max}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                    {formula && (
                      <p className="dashboard-guide__graph-formula">
                        <span className="dashboard-guide__graph-formula-label">
                          <FormattedMessage
                            id="analytics.guide.formulaLabel"
                            defaultMessage="Formula"
                          />
                          :
                        </span>{" "}
                        {formula}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
};

export default DashboardGuide;
